import Papa from 'papaparse';
import { Address } from '../types/index';

export interface ParsedData {
  addresses: Address[];
  stats: {
    total: number;
    withCoordinates: number;
    withoutCoordinates: number;
    ignored: number;
  };
}

// Enhanced column mapping with various possible names
const COLUMN_MAPPINGS = {
  businessName: [
    'business name', 'businessname', 'business_name', 'name', 'company', 'company name',
    'business', 'store', 'store name', 'shop', 'shop name', 'organization', 'org'
  ],
  address: [
    'address', 'street address', 'street_address', 'location', 'street', 'addr',
    'full address', 'full_address', 'address1', 'address 1', 'street1', 'street 1'
  ],
  latitude: [
    'latitude', 'lat', 'y', 'latitude_decimal', 'lat_decimal', 'decimal_latitude',
    'latitude_dd', 'lat_dd', 'y_coordinate', 'y_coord'
  ],
  longitude: [
    'longitude', 'lng', 'long', 'lon', 'x', 'longitude_decimal', 'lng_decimal',
    'decimal_longitude', 'longitude_dd', 'lng_dd', 'x_coordinate', 'x_coord'
  ]
};

// Find the best matching column name
function findColumn(headers: string[], possibleNames: string[]): string | null {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
  
  for (const possibleName of possibleNames) {
    const index = normalizedHeaders.indexOf(possibleName.toLowerCase());
    if (index !== -1) {
      return headers[index];
    }
  }
  
  // Try partial matches
  for (const possibleName of possibleNames) {
    const found = normalizedHeaders.find(h => h.includes(possibleName.toLowerCase()));
    if (found) {
      return headers[normalizedHeaders.indexOf(found)];
    }
  }
  
  return null;
}

// Parse coordinate value
function parseCoordinate(value: any): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  
  const num = typeof value === 'string' ? parseFloat(value.trim()) : Number(value);
  if (isNaN(num)) return undefined;
  
  return num;
}

// Validate coordinates
function validateCoordinates(lat?: number, lng?: number): boolean {
  if (lat === undefined || lng === undefined) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

// Parse CSV file
export function parseCSV(file: File): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const data = results.data as any[];
          const headers = Object.keys(data[0] || {});
          
          console.log('CSV Headers found:', headers);
          
          const processedData = processRows(data, headers);
          resolve(processedData);
        } catch (error) {
          console.error('CSV parsing error:', error);
          reject(error);
        }
      },
      error: (error) => {
        console.error('Papa parse error:', error);
        reject(error);
      }
    });
  });
}

// Process rows with intelligent column mapping
function processRows(data: any[], headers: string[]): ParsedData {
  const businessNameCol = findColumn(headers, COLUMN_MAPPINGS.businessName);
  const addressCol = findColumn(headers, COLUMN_MAPPINGS.address);
  const latCol = findColumn(headers, COLUMN_MAPPINGS.latitude);
  const lngCol = findColumn(headers, COLUMN_MAPPINGS.longitude);
  
  console.log('Column mapping:', {
    businessName: businessNameCol,
    address: addressCol,
    latitude: latCol,
    longitude: lngCol
  });
  
  let ignored = 0;
  let withCoordinates = 0;
  let withoutCoordinates = 0;
  
  const addresses: Address[] = data.map((row, index) => {
    const businessName = row[businessNameCol || ''] || '';
    const address = row[addressCol || ''] || '';
    const lat = parseCoordinate(row[latCol || '']);
    const lng = parseCoordinate(row[lngCol || '']);
    
    // Skip completely empty rows
    if (!businessName.trim() && !address.trim()) {
      ignored++;
      return null;
    }
    
    const hasValidCoordinates = validateCoordinates(lat, lng);
    
    if (hasValidCoordinates) {
      withCoordinates++;
    } else {
      withoutCoordinates++;
    }
    
    return {
      id: `import-${Date.now()}-${index}`,
      businessName: String(businessName).trim(),
      address: String(address).trim(),
      latitude: lat,
      longitude: lng,
      isSelected: false,
      isOnRoute: false,
      isDuplicate: false,
      isHighlighted: false
    };
  }).filter(addr => addr !== null) as Address[];
  
  return {
    addresses,
    stats: {
      total: addresses.length,
      withCoordinates,
      withoutCoordinates,
      ignored
    }
  };
}

// Enhanced tab-delimited parser
export function parseTabDelimited(text: string): ParsedData {
  const lines = text.trim().split('\n');
  
  if (lines.length === 0) {
    return {
      addresses: [],
      stats: { total: 0, withCoordinates: 0, withoutCoordinates: 0, ignored: 0 }
    };
  }
  
  // Check if first line looks like headers
  const firstLine = lines[0].toLowerCase();
  const hasHeaders = firstLine.includes('name') || firstLine.includes('address') || 
                    firstLine.includes('lat') || firstLine.includes('lng');
  
  const dataLines = hasHeaders ? lines.slice(1) : lines;
  let headers: string[] = [];
  
  if (hasHeaders) {
    headers = lines[0].split('\t').map(h => h.trim());
  }
  
  let withCoordinates = 0;
  let withoutCoordinates = 0;
  let ignored = 0;
  
  const addresses: Address[] = dataLines.map((line, index) => {
    const columns = line.split('\t').map(col => col.trim());
    
    let businessName = '';
    let address = '';
    let lat: number | undefined;
    let lng: number | undefined;
    
    if (hasHeaders) {
      // Map columns based on headers
      const businessNameCol = findColumn(headers, COLUMN_MAPPINGS.businessName);
      const addressCol = findColumn(headers, COLUMN_MAPPINGS.address);
      const latCol = findColumn(headers, COLUMN_MAPPINGS.latitude);
      const lngCol = findColumn(headers, COLUMN_MAPPINGS.longitude);
      
      businessName = columns[headers.indexOf(businessNameCol || '')] || '';
      address = columns[headers.indexOf(addressCol || '')] || '';
      lat = parseCoordinate(columns[headers.indexOf(latCol || '')]);
      lng = parseCoordinate(columns[headers.indexOf(lngCol || '')]);
    } else {
      // Assume order: business name, address, lat, lng
      businessName = columns[0] || '';
      address = columns[1] || '';
      lat = parseCoordinate(columns[2]);
      lng = parseCoordinate(columns[3]);
    }
    
    // Skip completely empty rows
    if (!businessName && !address) {
      ignored++;
      return null;
    }
    
    const hasValidCoordinates = validateCoordinates(lat, lng);
    
    if (hasValidCoordinates) {
      withCoordinates++;
    } else {
      withoutCoordinates++;
    }
    
    return {
      id: `tab-${Date.now()}-${index}`,
      businessName,
      address,
      latitude: lat,
      longitude: lng,
      isSelected: false,
      isOnRoute: false,
      isDuplicate: false,
      isHighlighted: false
    };
  }).filter(addr => addr !== null) as Address[];
  
  return {
    addresses,
    stats: {
      total: addresses.length,
      withCoordinates,
      withoutCoordinates,
      ignored
    }
  };
}

// Main file parser that handles CSV and TSV only
export async function parseFile(file: File): Promise<ParsedData> {
  const fileName = file.name.toLowerCase();
  
  if (fileName.endsWith('.csv')) {
    return parseCSV(file);
  } else if (fileName.endsWith('.txt') || fileName.endsWith('.tsv')) {
    const text = await file.text();
    return parseTabDelimited(text);
  } else {
    throw new Error('Unsupported file format. Please use CSV or TXT files. For Excel files, please save as CSV first.');
  }
}